import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminHeaders } from '../../lib/adminAuth';
import DeviceSetupPage from './DeviceSetupPage';
import MailTemplatesPage from './MailTemplatesPage';
import CouponSettings from './CouponSettings';
import './SettingsPage.css';

import { tx } from '../../lib/adminI18n';
type SettingsTab = 'basic' | 'mail' | 'device' | 'coupon';
const TABS: { key: SettingsTab; label: string }[] = [
  { key: 'basic', get label() { return tx('set.0'); } },
  { key: 'mail', get label() { return tx('set.1'); } },
  { key: 'device', get label() { return tx('set.2'); } },
  { key: 'coupon', get label() { return tx('cp.tab'); } },
];

// ------------------------------------------------------------
// 管理画面: 施設設定  /admin/settings
//   施設情報・振込先など、予約確認/振込案内メールに差し込む settings を編集する。
//   対象キーはバックエンド /api/admin/settings のホワイトリストと一致させること。
//   ※ メール送信タイミング(mail_*_trigger_*)は施設設定の「メールテンプレート」タブで管理。
// ------------------------------------------------------------

type SettingsMap = Record<string, string>;

interface FieldDef {
  key: string;
  label: string;
  type?: 'text' | 'email' | 'number' | 'tel';
  placeholder?: string;
  hint?: string;
}

const FACILITY_FIELDS: FieldDef[] = [
  { key: 'site_title', get label() { return tx('set.41'); }, get placeholder() { return tx('set.42'); }, get hint() { return tx('set.43'); } },
  { key: 'admin_title', get label() { return tx('set.38'); }, get placeholder() { return tx('set.39'); }, get hint() { return tx('set.40'); } },
  { key: 'facility_name', get label() { return tx('set.3'); }, get placeholder() { return tx('set.4'); }, get hint() { return tx('set.5'); } },
  { key: 'facility_address', get label() { return tx('set.6'); }, get placeholder() { return tx('set.7'); } },
  { key: 'facility_phone', get label() { return tx('set.8'); }, type: 'tel', get placeholder() { return tx('set.9'); } },
  { key: 'facility_email', get label() { return tx('set.10'); }, type: 'email', get placeholder() { return tx('set.11'); }, get hint() { return tx('set.12'); } },
];

const BANK_FIELDS: FieldDef[] = [
  { key: 'bank_name', get label() { return tx('set.13'); }, get placeholder() { return tx('set.14'); } },
  { key: 'bank_branch', get label() { return tx('set.15'); }, get placeholder() { return tx('set.16'); } },
  { key: 'bank_account_type', get label() { return tx('set.17'); }, get placeholder() { return tx('set.18'); } },
  { key: 'bank_account_number', get label() { return tx('set.19'); }, get placeholder() { return tx('set.20'); } },
  { key: 'bank_account_name', get label() { return tx('set.21'); }, get placeholder() { return tx('set.22'); } },
];

const OTHER_FIELDS: FieldDef[] = [
  { key: 'transfer_deadline_hours', get label() { return tx('set.23'); }, type: 'number', get placeholder() { return tx('set.24'); }, get hint() { return tx('set.25'); } },
];

const ALL_FIELDS = [...FACILITY_FIELDS, ...BANK_FIELDS, ...OTHER_FIELDS];

export default function SettingsPage() {
  // タブ（?tab=device で受付端末タブを直接開く。旧URL /admin/checkin-device からもここへ来る）
  const [params, setParams] = useSearchParams();
  const q = params.get('tab');
  const tab: SettingsTab = q === 'device' || q === 'mail' || q === 'coupon' ? q : 'basic';
  const setTab = (k: SettingsTab) => {
    const next = new URLSearchParams(params);
    if (k === 'basic') next.delete('tab'); else next.set('tab', k);
    setParams(next, { replace: true });
  };
  const [form, setForm] = useState<SettingsMap>({});
  const [saved, setSaved] = useState<SettingsMap>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const dirty = JSON.stringify(form) !== JSON.stringify(saved);

  const load = useCallback(async () => {
    setLoading(true); setMsg(null);
    try {
      const res = await fetch('/api/admin/settings', { headers: adminHeaders(), credentials: 'include' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setMsg({ ok: false, text: tx('set.26') }); return; }
      const s: SettingsMap = {};
      for (const f of ALL_FIELDS) s[f.key] = (d.settings && d.settings[f.key]) || '';
      setForm(s); setSaved(s);
    } catch {
      setMsg({ ok: false, text: tx('set.26') });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (key: string, value: string) => setForm((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const res = await fetch('/api/admin/settings', {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        credentials: 'include',
        body: JSON.stringify({ settings: form }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setMsg({ ok: false, text: d.error || tx('set.27') }); return; }
      setSaved(form);
      // 管理画面の左上タイトルをすぐ反映する
      window.dispatchEvent(new CustomEvent('admin-title-changed', { detail: form.admin_title || '' }));
      setMsg({ ok: true, text: tx('set.28') });
    } catch (e: any) {
      setMsg({ ok: false, text: e?.message || tx('set.27') });
    } finally { setSaving(false); }
  };

  const renderField = (f: FieldDef) => (
    <div className="set-group" key={f.key}>
      <label className="set-label" htmlFor={`set-${f.key}`}>{f.label}</label>
      <input
        id={`set-${f.key}`}
        className="set-input"
        type={f.type === 'number' ? 'text' : (f.type || 'text')}
        inputMode={f.type === 'number' ? 'numeric' : undefined}
        value={form[f.key] ?? ''}
        placeholder={f.placeholder}
        onChange={(e) => setField(f.key, e.target.value)}
      />
      {f.hint && <small className="set-hint">{f.hint}</small>}
    </div>
  );

  return (
    <div className={`settings-page ${tab === 'mail' ? 'is-wide' : ''}`}>
      <div className="set-head">
        <h1>{tx('set.29')}</h1>
        {tab === 'basic' && <p className="set-sub">{tx('set.30')}</p>}
      </div>

      <div className="set-tabs" role="tablist">
        {TABS.map((x) => (
          <button key={x.key} type="button" role="tab" aria-selected={tab === x.key}
            className={`set-tab ${tab === x.key ? 'active' : ''}`} onClick={() => setTab(x.key)}>{x.label}</button>
        ))}
      </div>

      {tab === 'device' ? (
        <section className="set-section set-device"><DeviceSetupPage embedded /></section>
      ) : tab === 'mail' ? (
        <MailTemplatesPage embedded />
      ) : tab === 'coupon' ? (
        <CouponSettings />
      ) : loading ? <p className="set-loading">{tx('set.31')}</p> : (
        <>
          <section className="set-section">
            <h2 className="set-section-title">{tx('set.32')}</h2>
            <div className="set-grid">{FACILITY_FIELDS.map(renderField)}</div>
          </section>

          <section className="set-section">
            <h2 className="set-section-title">{tx('set.33')}</h2>
            <div className="set-grid">{BANK_FIELDS.map(renderField)}</div>
          </section>

          <section className="set-section">
            <h2 className="set-section-title">{tx('set.34')}</h2>
            <div className="set-grid">{OTHER_FIELDS.map(renderField)}</div>
          </section>

          {msg && <div className={msg.ok ? 'set-msg set-msg-ok' : 'set-msg set-msg-err'}>{msg.text}</div>}

          <div className="set-actions">
            <button className="set-btn set-btn-ghost" disabled={!dirty || saving} onClick={() => { setForm(saved); setMsg(null); }}>
              {tx('set.35')}
            </button>
            <button className="set-btn set-btn-primary" disabled={!dirty || saving} onClick={save}>
              {saving ? tx('set.36') : tx('set.37')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
